import { creatorbase, creatorbaseQueries } from '@round/api';
import { useCallback } from 'react';
import {
    useBulkCreatePosts,
    useInvalidateBulkUploads,
    useInvalidatePosts,
} from '@round/creatorbase-helpers/api-hooks';
import { showNotification } from 'helpers';
import useRefreshProjectCampaignStats from '../useRefreshProjectCampaignStats';
import { getProjectDetailsPostsParams } from './getProjectDetailsPostsParams';
export default function useBulkCreateProjectDetailsPost() {
    const invalidatePosts = useInvalidatePosts();
    const invalidateBulkUploads = useInvalidateBulkUploads();
    const refreshAllStats = useRefreshProjectCampaignStats();

    const { mutateAsync } = useBulkCreatePosts({
        onSuccess: (data: creatorbase.BulkCreatePostsResponseData | creatorbase.BulkCreatePostsAcceptedData) => {
            if ('upload_id' in data) {
                // Async upload accepted -- nothing was created synchronously, but the upload
                // itself should show up as soon as the Report tab is opened, not after its
                // staleTime lapses.
                invalidateBulkUploads();
                return;
            }

            const createdPost = data.created[0];
            if (!createdPost) {
                return;
            }

            invalidatePosts(getProjectDetailsPostsParams(createdPost.campaign_id));
            refreshAllStats(createdPost.campaign_id, createdPost.platform).catch(() => {
                showNotification('Posts created but could not refresh data', 'error');
            });
        },
    });

    const bulkCreatePosts: typeof creatorbase.bulkCreatePosts = useCallback(
        async (data) => {
            try {
                const responseData = await mutateAsync(data);
                if ('upload_id' in responseData) {
                    return { status: 202, data: responseData };
                }

                return { status: 201, data: responseData };
            } catch (error) {
                if (
                    error instanceof Error &&
                    creatorbaseQueries.posts.utils.bulkCreate.isExpectedErrorResponse(error)
                ) {
                    return error.response;
                }

                throw error;
            }
        },
        [mutateAsync]
    );

    return { bulkCreatePosts };
}
